import { useEffect, useMemo, useState } from 'react';
import { IconSpark } from '../icons';

export type SparkyMoment =
  | 'welcome'
  | 'survey-start'
  | 'survey-done'
  | 'build-start'
  | 'build-done'
  | 'first-publish'
  | 'budget-low'
  | 'budget-empty'
  | 'error-explained';

const MOMENTS: Record<SparkyMoment, { title: string; body: string }> = {
  welcome: {
    title: 'Hi, I am Sparky',
    body: 'Tell me what you want to make and I will build a first version. You can change anything afterwards — nothing here is permanent.',
  },
  'survey-start': {
    title: 'A few quick questions',
    body: 'These help me build something closer to what you pictured. There are no wrong answers.',
  },
  'survey-done': {
    title: 'That is everything I need',
    body: 'Press Build and I will put the first version together. It will not be perfect — that is the point.',
  },
  'build-start': {
    title: 'Building now',
    body: 'Watch the preview on the right. I will say what I am doing as I go.',
  },
  'build-done': {
    title: 'Have a play with it',
    body: 'Try it properly before changing anything — you will spot things that are hard to see in the code.',
  },
  'first-publish': {
    title: 'It is live',
    body: 'Anyone with your link can play it now. Share the QR code and watch someone else use what you made.',
  },
  'budget-low': {
    title: 'Getting low on Tokens',
    body: 'One goal per prompt gets the most out of what is left. Planning, fixing and testing are always free.',
  },
  'budget-empty': {
    title: 'Out of Tokens for now',
    body: 'You can still edit text, look at the code, and play what you built. Your work is saved, and finishing lessons in your school earns more.',
  },
  'error-explained': {
    title: 'Something broke',
    body: 'This happens to every builder. Tell me what should happen instead, in your own words.',
  },
};

interface SparkyPanelProps {
  moment: SparkyMoment;
  /** phase narration while a job runs — overrides the moment copy */
  narration?: string | null;
  /** explicit message (e.g. the learning moment after auto-fix gives up) — beats everything */
  custom?: { title: string; body: string } | null;
  onDismiss: () => void;
}

/**
 * Sparky's surface (spec 5b.1). Deliberately a banner, never a modal: a guide that blocks the screen
 * gets dismissed and ignored. Each moment is shown once and stays dismissed across sessions (see
 * lib/notices) — repeating yourself is how a helper becomes noise.
 */
export function SparkyPanel({ moment, narration, custom, onDismiss }: SparkyPanelProps) {
  const [seen, setSeen] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSeen(prev => (prev.has(moment) ? prev : new Set(prev).add(moment)));
  }, [moment]);

  const content = useMemo(() => custom ?? MOMENTS[moment], [custom, moment]);

  if (narration && !custom) {
    return (
      <div className="flex items-start gap-2 border-b border-line bg-page px-4 py-2">
        <span className="mt-0.5 shrink-0 text-accent">
          <IconSpark width={15} height={15} />
        </span>
        <p className="text-sm">{narration}</p>
      </div>
    );
  }

  /*
    Sparky speaks rarely and only about things the learner cannot see for themselves ("it is live",
    an explained error), so the banner is allowed to be loud: the brand yellow with deep-blue type,
    which is the marketing site's pairing and reads as an event rather than as more chrome. It was
    previously page-gray on a gray studio and disappeared into the toolbar above it.
    The yellow is identical in both themes, and blue-on-yellow keeps its contrast in both.
  */
  return (
    <div className="anim-in flex items-start gap-3 bg-sun px-4 py-3 text-brand">
      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand text-sun">
        <IconSpark width={14} height={14} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold">{content.title}</p>
        <p className="text-sm font-medium text-brand/80">{content.body}</p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded-lg px-2.5 py-1 text-xs font-bold text-brand/70 transition hover:bg-brand/10 hover:text-brand"
      >
        Got it
      </button>
    </div>
  );
}

/** Phase → learner-facing narration. Mirrors `box-api/src/lib/sparky.ts` (keep the two in step). */
export function narratePhase(phase: string | null | undefined): string {
  if (!phase) return 'Getting started';
  const map: Record<string, string> = {
    select_skills: 'Working out what your idea needs',
    plan: 'Sketching the plan for your app',
    html: 'Building the screens',
    images: 'Drawing the pictures',
    css: 'Making it look right',
    js: 'Wiring up the buttons so things actually work',
    persist: 'Saving everything',
    scope: 'Finding the parts I need to change',
    delta: 'Making your change',
    done: 'Finished',
  };
  return map[phase] ?? 'Working on it';
}
